import useFetch from "../../clients/features/get_withcredentials";
import { useParams } from "react-router-dom";
import { useState, useMemo } from "react";
import { TbCalendar, TbList, TbChevronLeft, TbChevronRight } from "react-icons/tb";

const COLORS = [
  { bg: "bg-blue-100", text: "text-blue-700", bar: "bg-blue-400" },
  { bg: "bg-violet-100", text: "text-violet-700", bar: "bg-violet-400" },
  { bg: "bg-amber-100", text: "text-amber-700", bar: "bg-amber-400" },
  { bg: "bg-rose-100", text: "text-rose-700", bar: "bg-rose-400" },
  { bg: "bg-cyan-100", text: "text-cyan-700", bar: "bg-cyan-400" },
  { bg: "bg-emerald-100", text: "text-emerald-700", bar: "bg-emerald-400" },
];

const STATUS = {
  confirmed: { label: "Confirmée", dot: "bg-green-500", bg: "bg-green-50", text: "text-green-700" },
  pending: { label: "En attente", dot: "bg-yellow-500", bg: "bg-yellow-50", text: "text-yellow-700" },
  cancelled: { label: "Annulée", dot: "bg-red-400", bg: "bg-red-50", text: "text-red-600" },
  pending_cancel: { label: "Annulation demandée", dot: "bg-orange-500", bg: "bg-orange-50", text: "text-orange-700" },
  no_show: { label: "No-show", dot: "bg-gray-400", bg: "bg-gray-100", text: "text-gray-600" },
};

const getColorByRoom = (title = "") => {
  const index = title.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % COLORS.length;
  return COLORS[index];
};

const normalize = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const nights = (checkIn, checkOut) =>
  Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));

export default function Book() {
  const { hotelId } = useParams();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [view, setView] = useState("calendar"); // "calendar" | "list"
  const [listFilter, setListFilter] = useState("all");

  const { data = [], loading } = useFetch(
    `https://api.komorotel.com/api/reservation/hotel/${hotelId}`
  );

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));

  const changeMonth = (dir) => setCurrentDate(new Date(year, month + dir, 1));

  const monthName = currentDate.toLocaleString("fr-FR", { month: "long", year: "numeric" });

  const monthReservations = useMemo(() => {
    return data.filter(r => {
      const start = normalize(r.checkInDate);
      const end = normalize(r.checkOutDate);
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);
      return start <= monthEnd && end >= monthStart;
    });
  }, [data, year, month]);

  const getReservationsForDay = (date) => {
    const d = normalize(date);
    return monthReservations.filter(r => {
      const start = normalize(r.checkInDate);
      const end = normalize(r.checkOutDate);
      return d >= start && d < end;
    });
  };

  // Stats
  const stats = useMemo(() => ({
    total: monthReservations.length,
    confirmed: monthReservations.filter(r => r.status === "confirmed").length,
    pending: monthReservations.filter(r => r.status === "pending").length,
    revenue: monthReservations
      .filter(r => r.status === "confirmed")
      .reduce((sum, r) => sum + (r.totalPrice || 0), 0),
  }), [monthReservations]);

  // Liste filtrée
  const listReservations = useMemo(() => {
    let filtered = listFilter === "all" ? data : data.filter(r => r.status === listFilter);
    return filtered.sort((a, b) => new Date(b.checkInDate) - new Date(a.checkInDate));
  }, [data, listFilter]);

  // Chambres uniques pour légende
  const uniqueRooms = useMemo(() => {
    const map = new Map();
    monthReservations.forEach(r => {
      if (r.roomId?.title) map.set(r.roomId.title, true);
    });
    return Array.from(map.keys());
  }, [monthReservations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#3A9D23] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-6">

      {/* HEADER */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Réservations</h1>
          <p className="text-sm text-gray-400 mt-0.5">Calendrier et liste de vos séjours</p>
        </div>

        {/* Toggle vue */}
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setView("calendar")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              view === "calendar" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <TbCalendar className="text-base" /> Calendrier
          </button>
          <button
            onClick={() => setView("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              view === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <TbList className="text-base" /> Liste
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Ce mois", value: stats.total, sub: "réservations" },
          { label: "Confirmées", value: stats.confirmed, sub: "séjours", color: "text-green-600" },
          { label: "En attente", value: stats.pending, sub: "à traiter", color: "text-yellow-600" },
          { label: "Revenus", value: `${stats.revenue.toLocaleString()} €`, sub: "confirmés", color: "text-[#3A9D23]" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-xs text-gray-400 mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color || "text-gray-900"}`}>{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* VUE CALENDRIER */}
      {view === "calendar" && (
        <div className="flex flex-col gap-4">

          {/* Navigation + légende */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => changeMonth(-1)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition">
                <TbChevronLeft className="text-gray-600" />
              </button>
              <h2 className="text-base font-bold text-gray-900 capitalize min-w-[160px] text-center">
                {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
              </h2>
              <button onClick={() => changeMonth(1)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition">
                <TbChevronRight className="text-gray-600" />
              </button>
            </div>

            {/* Légende chambres */}
            <div className="flex flex-wrap gap-2">
              {uniqueRooms.map(title => {
                const color = getColorByRoom(title);
                return (
                  <span key={title} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color.bg} ${color.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${color.bar}`} />
                    {title}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Grille */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-100">
              {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => (
                <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {Array.from({ length: offset }).map((_, i) => (
                <div key={`e-${i}`} className="min-h-[110px] border-b border-r border-gray-50 bg-gray-50/30" />
              ))}

              {days.map((day, i) => {
                const isToday = normalize(day).getTime() === normalize(new Date()).getTime();
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const dayRes = getReservationsForDay(day);
                const isLastCol = (offset + i + 1) % 7 === 0;
                const isPast = normalize(day) < normalize(new Date());

                return (
                  <div
                    key={i}
                    className={`
                      min-h-[110px] p-2 border-b border-r border-gray-50 transition-colors
                      ${isLastCol ? "border-r-0" : ""}
                      ${isWeekend ? "bg-gray-50/40" : "bg-white"}
                      ${isPast && !isToday ? "opacity-60" : ""}
                    `}
                  >
                    <div className={`
                      w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold mb-1.5 transition
                      ${isToday ? "bg-[#3A9D23] text-white" : "text-gray-600"}
                    `}>
                      {day.getDate()}
                    </div>

                    <div className="flex flex-col gap-0.5">
                      {dayRes.slice(0, 3).map((r, ri) => {
                        const color = getColorByRoom(r.roomId?.title);
                        const isStart = normalize(day).getTime() === normalize(r.checkInDate).getTime();
                        const isEnd = normalize(day).getTime() === normalize(new Date(new Date(r.checkOutDate).getTime() - 86400000)).getTime();

                        return (
                          <div
                            key={ri}
                            onClick={() => setSelectedReservation(r)}
                            title={`${r.roomId?.title} · ${r.guest?.firstName} ${r.guest?.lastName}`}
                            className={`
                              px-1.5 py-0.5 text-xs font-medium cursor-pointer truncate transition
                              ${color.bg} ${color.text}
                              ${isStart ? "rounded-l-md" : ""}
                              ${isEnd ? "rounded-r-md" : ""}
                              hover:opacity-75
                            `}
                          >
                            {isStart
                              ? `${r.roomId?.title || "Chambre"} · ${r.guest?.firstName || ""}`
                              : <span className="opacity-0">·</span>
                            }
                          </div>
                        );
                      })}
                      {dayRes.length > 3 && (
                        <p className="text-xs text-gray-400 pl-1 mt-0.5">
                          +{dayRes.length - 3}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* VUE LISTE */}
      {view === "list" && (
        <div className="flex flex-col gap-4">

          {/* Filtres statut */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "Toutes", count: data.length },
              { key: "confirmed", label: "Confirmées", count: data.filter(r => r.status === "confirmed").length },
              { key: "pending", label: "En attente", count: data.filter(r => r.status === "pending").length },
              { key: "pending_cancel", label: "Annulations", count: data.filter(r => r.status === "pending_cancel").length },
              { key: "no_show", label: "No-show", count: data.filter(r => r.status === "no_show").length },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setListFilter(f.key)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
                  listFilter === f.key
                    ? "bg-gray-900 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {f.label} <span className="opacity-60 ml-0.5">({f.count})</span>
              </button>
            ))}
          </div>

          {/* Liste */}
          <div className="flex flex-col gap-3">
            {listReservations.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-400 text-sm">
                Aucune réservation
              </div>
            )}
            {listReservations.map((r) => {
              const s = STATUS[r.status] || STATUS.confirmed;
              const n = nights(r.checkInDate, r.checkOutDate);
              const today = normalize(new Date());
              const checkIn = normalize(r.checkInDate);
              const checkOut = normalize(r.checkOutDate);
              const isOngoing = today >= checkIn && today < checkOut;
              const isUpcoming = today < checkIn;

              return (
                <div
                  key={r._id}
                  onClick={() => setSelectedReservation(r)}
                  className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all"
                >
                  {/* Barre couleur chambre */}
                  <div className={`w-1 self-stretch rounded-full ${getColorByRoom(r.roomId?.title).bar}`} />

                  {/* Dates */}
                  <div className="text-center min-w-[60px]">
                    <p className="text-lg font-bold text-gray-900">
                      {new Date(r.checkInDate).getDate()}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(r.checkInDate).toLocaleString("fr-FR", { month: "short" })}
                    </p>
                  </div>

                  <div className="text-gray-200 text-lg">→</div>

                  <div className="text-center min-w-[60px]">
                    <p className="text-lg font-bold text-gray-900">
                      {new Date(r.checkOutDate).getDate()}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(r.checkOutDate).toLocaleString("fr-FR", { month: "short" })}
                    </p>
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {r.guest?.firstName} {r.guest?.lastName}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {r.roomId?.title} · {n} nuit{n > 1 ? "s" : ""}
                    </p>
                  </div>

                  {/* Badge séjour */}
                  {isOngoing && (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-600 font-medium shrink-0">
                      En cours
                    </span>
                  )}
                  {isUpcoming && (
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 font-medium shrink-0">
                      À venir
                    </span>
                  )}

                  {/* Statut */}
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold shrink-0 ${s.bg} ${s.text}`}>
                    {s.label}
                  </span>

                  {/* Prix */}
                  <p className="font-bold text-gray-900 shrink-0">
                    {r.totalPrice} €
                  </p>

                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* MODAL détail */}
      {selectedReservation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setSelectedReservation(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm">

            {/* Header modal */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Détail réservation</h3>
              <button
                onClick={() => setSelectedReservation(null)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-500"
              >
                ×
              </button>
            </div>

            {/* Contenu modal */}
            <div className="px-5 py-4 flex flex-col gap-0">
              {[
                { label: "Client", value: `${selectedReservation.guest?.firstName} ${selectedReservation.guest?.lastName}` },
                { label: "Email", value: selectedReservation.guest?.email },
                { label: "Téléphone", value: selectedReservation.guest?.phone || "—" },
                { label: "Chambre", value: selectedReservation.roomId?.title || "—" },
                {
                  label: "Dates",
                  value: `${new Date(selectedReservation.checkInDate).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} → ${new Date(selectedReservation.checkOutDate).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`
                },
                { label: "Nuits", value: `${nights(selectedReservation.checkInDate, selectedReservation.checkOutDate)} nuit${nights(selectedReservation.checkInDate, selectedReservation.checkOutDate) > 1 ? "s" : ""}` },
                { label: "Tarif", value: selectedReservation.ratePlanSnapshot?.name || "Standard" },
                { label: "Réf", value: selectedReservation.reservationId },
              ].map((item, i) => (
                <div key={i} className="flex justify-between text-sm py-2.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="font-semibold text-gray-900 text-right max-w-[60%] truncate">{item.value}</span>
                </div>
              ))}

              <div className="flex justify-between pt-3">
                <span className="text-gray-400 text-sm">Total</span>
                <span className="font-bold text-xl text-gray-900">{selectedReservation.totalPrice} €</span>
              </div>
            </div>

            {/* Statut */}
            <div className="px-5 pb-5">
              {(() => {
                const s = STATUS[selectedReservation.status] || STATUS.confirmed;
                return (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                    {s.label}
                  </span>
                );
              })()}
            </div>

          </div>
        </div>
      )}

    </div>
  );
}